import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, PropsWithChildren } from 'react';

import { getUser } from 'utils';
import { Config, Routes } from 'config';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { updateAuthUser, selectIsLoggedIn } from 'store/slices/auth';

const { Home, Auth, Dashboard } = Routes;

const publicRoutes = Config.isLoginByGuestId
  ? ([Auth.Login] as const)
  : ([...Object.values(Auth), Home] as const);

export function AuthGuard({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  const isHomePage = pathname === '/';
  const isPublicRoute = publicRoutes.includes(pathname);
  const shouldRedirectToLogin = !isLoggedIn && !isPublicRoute;
  const shouldRedirectToDashboard = isLoggedIn && (isPublicRoute || isHomePage);
  const isBlockingRender =
    !isAuthChecked || shouldRedirectToLogin || shouldRedirectToDashboard;

  useEffect(() => {
    const user = getUser();
    if (user) {
      dispatch(updateAuthUser(user));
    }
    setIsAuthChecked(true);
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthChecked) return;

    if (shouldRedirectToLogin) {
      navigate(Auth.Login);
      return;
    }

    if (shouldRedirectToDashboard) {
      navigate(Dashboard.Home);
      return;
    }
  }, [
    navigate,
    pathname,
    isLoggedIn,
    isAuthChecked,
    shouldRedirectToLogin,
    shouldRedirectToDashboard,
  ]);

  if (isBlockingRender) {
    return null;
  }

  return children;
}
